export const isClient = typeof window === "object"

export const formatSensaiUrl = (url: string) => {
  const isSensai = url && (url as String).startsWith("https://sens.ai")
  let formatedUrl = isSensai || url.indexOf("/") === 0 ? url : `/${url}`
  formatedUrl =
    formatedUrl === "/" || formatedUrl.endsWith("/")
      ? formatedUrl
      : `${formatedUrl}/`
  return formatedUrl
}

export const cleanSlug = (slug: string, isPage = false) => {
  if (!slug || slug === "") {
    return isPage ? "/" : ""
  }
  if (!slug.includes(".")) {
    // The path only. Fragments match ids case-sensitively, so lowercasing
    // `#Nick` would stop it reaching the section with id="Nick".
    const hashIndex = slug.indexOf("#")
    slug =
      hashIndex === -1
        ? slug.toLowerCase()
        : slug.slice(0, hashIndex).toLowerCase() + slug.slice(hashIndex)
  }
  if (slug.length > 1 && slug.endsWith("/")) {
    //trim off trailing slash
    slug = `${slug.slice(0, slug.length - 1)}`
  }
  if (slug.startsWith("#")) {
    //relative anchor link
    return slug
  }

  return slug.startsWith("/") || slug.startsWith("http")
    ? `${slug}`
    : `/${slug}`
}

export const formatDate = ({
  date,
  fullMonth,
}: {
  date: Date
  fullMonth: boolean
}): string => {
  const dateStr = new Intl.DateTimeFormat("en", {
    month: fullMonth ? "long" : "short",
    day: "2-digit",
    year: "numeric",
  }).format(date)
  return dateStr
}

export const formatCurrencyString = (
  {
    value,
    currency,
  }: {
    value: number
    currency: string
  },
  renderDollar = true,
  showCurrency = true
) => {
  const language = currency === "CAD" ? "en-CA" : "en-US"

  const numberFormat = new Intl.NumberFormat(language, {
    style: showCurrency ? "currency" : "decimal",
    currency,
    currencyDisplay: showCurrency ? "symbol" : "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const parts = numberFormat.formatToParts(value)
  let zeroDecimalCurrency = true

  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i]
    if (part.type === "decimal") {
      zeroDecimalCurrency = false
      break
    }
  }

  let val = zeroDecimalCurrency ? value : value / 100

  if (!renderDollar) {
    return numberFormat
      .format(parseFloat(val.toFixed(2)))
      .replace(/\D00(?=\D*$)/, "")
      .replace("$", "")
  }
  if (!showCurrency && renderDollar) {
    return (
      "$" +
      numberFormat
        .format(parseFloat(val.toFixed(2)))
        .replace(/\D00(?=\D*$)/, "")
    )
  }

  return numberFormat
    .format(parseFloat(val.toFixed(2)))
    .replace(/\D00(?=\D*$)/, "")
}

export const getRandomNum = (min: number, max: number): number => {
  min = Math.ceil(min)
  max = Math.floor(max)
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// All 27 EU member states, each listed by ISO 3166-1 alpha-2, alpha-3 and
// English name, because the value reaching isInEU comes from more than one
// source: Layout.tsx writes `country_for_inventory` from an edge `ctx` cookie in
// one path and from a geo API's `country_name` in another.
//
// This list previously held only 21 members, and Italy only in its alpha-3 form.
// Because the stored value is usually a country NAME, that meant visitors from
// Austria, Belgium, Bulgaria, Croatia, Cyprus, Czechia and Italy fell through to
// the non-EU branch in CookiePolicy.tsx and were auto-accepted with no banner.
// Matching is normalised (trim + uppercase) so a differently-cased source cannot
// reintroduce the same class of gap.
//
// SCOPE: EU only. GDPR also covers the EEA (Iceland, Liechtenstein, Norway) and
// the UK has its own UK GDPR — those are deliberately NOT included here, since
// widening the consent net is a policy decision rather than a bug fix.
const EU_COUNTRIES = [
  "AT",
  "AUT",
  "AUSTRIA",
  "BE",
  "BEL",
  "BELGIUM",
  "BG",
  "BGR",
  "BULGARIA",
  "HR",
  "HRV",
  "CROATIA",
  "CY",
  "CYP",
  "CYPRUS",
  "CZ",
  "CZE",
  "CZECHIA",
  "CZECH REPUBLIC",
  "DK",
  "DNK",
  "DENMARK",
  "EE",
  "EST",
  "ESTONIA",
  "FI",
  "FIN",
  "FINLAND",
  "FR",
  "FRA",
  "FRANCE",
  "DE",
  "DEU",
  "GERMANY",
  "GR",
  "GRC",
  "GREECE",
  "HU",
  "HUN",
  "HUNGARY",
  "IE",
  "IRL",
  "IRELAND",
  "IT",
  "ITA",
  "ITALY",
  "LV",
  "LVA",
  "LATVIA",
  "LT",
  "LTU",
  "LITHUANIA",
  "LU",
  "LUX",
  "LUXEMBOURG",
  "MT",
  "MLT",
  "MALTA",
  "NL",
  "NLD",
  "NETHERLANDS",
  "THE NETHERLANDS",
  "PL",
  "POL",
  "POLAND",
  "PT",
  "PRT",
  "PORTUGAL",
  "RO",
  "ROU",
  "ROMANIA",
  "SK",
  "SVK",
  "SLOVAKIA",
  "SI",
  "SVN",
  "SLOVENIA",
  "ES",
  "ESP",
  "SPAIN",
  "SE",
  "SWE",
  "SWEDEN",
]

export const isInEU = (countryCode: string) =>
  !!countryCode && EU_COUNTRIES.includes(countryCode.trim().toUpperCase())
